import { useEffect, useState } from 'react';
import { Fragment, FunctionComponent } from 'react';
import { Drawer, Button, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { isImageRegExp, isPDFRegExp } from '~src/utils/file';
import ImageViewer from './ImageViewer/ImageViewer';
import { PdfViewer } from './PdfViewer/PdfViewer';
import { SVFViewer } from './SVFViewer/SVFViewer';
import {
  DbModuleFile,
  DbTenderInsideSVFFile,
  DbTenderSVFAttachment,
} from '~src/types/db';

type ViewerProps = {
  fileUrl: string;
  fileExtension: string | undefined;
  fileToView?: DbModuleFile | DbTenderSVFAttachment | DbTenderInsideSVFFile;
  onCloseSVFViewer?: () => void;
  // Drawer mode props
  mode?: 'inline' | 'drawer';
  drawerVisible?: boolean;
  onDrawerClose?: () => void;
  drawerTitle?: string;
  drawerWidth?: number | string;
  showDownloadButton?: boolean;
  onDownload?: () => void;
  onInternalClose?: () => void;
  isLoadingUrl?: boolean;
};

const Viewer: FunctionComponent<ViewerProps> = ({
  fileUrl,
  fileExtension,
  fileToView,
  onCloseSVFViewer,
  mode = 'inline',
  drawerVisible = false,
  onDrawerClose,
  drawerTitle = 'File Preview',
  drawerWidth = 800,
  showDownloadButton = false,
  onDownload,
  onInternalClose,
  isLoadingUrl = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
  }, [fileExtension]);

  useEffect(() => {
    // Only stop loading if parent says URL is ready (!isLoadingUrl)
    if (!isLoadingUrl) {
      setIsLoading(false);
    }
  }, [fileUrl, isLoadingUrl]);

  // ==SVF support
  // Support both shapes:
  // 1) regular module file -> versions[0] || Archive
  // 2) tender root attachment -> top-level svf fields || Tender Documents ROOT
  const sourceType = (fileToView as any)?.sourceType;

  const hasVersionSVFSupport =
    fileToView?.versions?.[0]?.is_svf_convertible ||
    fileToView?.versions?.[0]?.extension_svf_model?.state === 7;

  const hasTopLevelSVFSupport =
    (fileToView as any)?.is_svf_convertible === true ||
    (fileToView as any)?.extension_svf_model?.state === 7;

  const hasBridgedSVFSupport =
    [
      'tender-admin-root-svf',
      'tender-admin-inside-svf',
      'tender-unassigned-inside-svf',
      'tender-share-root-svf',
      'tender-share-inside-svf',
    ].includes(sourceType) &&
    (fileToView as any)?.extension_svf_model?.state === 7;

  const canOpenSVFViewer =
    !!fileToView &&
    !isLoading &&
    (hasVersionSVFSupport || hasTopLevelSVFSupport || hasBridgedSVFSupport);

  // Render viewer content
  const renderViewerContent = () => {
    if (!fileExtension) {
      return null;
    }

    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '300px',
          }}>
          <Spin size="large" />
        </div>
      );
    }

    if (isImageRegExp.test(fileExtension)) {
      return (
        <ImageViewer
          fileUrl={fileUrl}
          onInternalClose={mode === 'drawer' ? onDrawerClose : onInternalClose}
        />
      );
    } else if (isPDFRegExp.test(fileExtension)) {
      return (
        <PdfViewer
          fileUrl={fileUrl}
          onInternalClose={mode === 'drawer' ? onDrawerClose : onInternalClose}
        />
      );
    } else if (canOpenSVFViewer) {
      return (
        <SVFViewer fileToView={fileToView as any} onClose={onCloseSVFViewer} />
      );
    } else {
      return null;
    }
  };

  // Inline mode: render content directly (current behavior)
  if (mode === 'inline') {
    return <Fragment>{renderViewerContent()}</Fragment>;
  }

  // Drawer mode: wrap content in Ant Design Drawer
  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={onDrawerClose}
      visible={drawerVisible}
      width={drawerWidth}
      destroyOnClose={true}
      maskClosable={true}
      bodyStyle={{ padding: 0 }}
      footer={
        showDownloadButton && onDownload ? (
          <div style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onDownload}>
              Download
            </Button>
          </div>
        ) : null
      }>
      {renderViewerContent()}
    </Drawer>
  );
};

export default Viewer;
